import {MutableRefObject, useCallback, useEffect, useRef, useState} from 'react';
import {useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/SaveAlt';
import {Button, Stack, Typography} from '@mui/material';
import {translations} from '../../../../../../i18n/translations';
import {UsersRepository} from '../../../../../../repositories/users';
import {UserType} from '../../../../../../backend/enums/user-type';
import {AffiliatedUser} from '../../../../../../backend/responses/affiliated-user';
import {
  PaginatedTableData,
  PaginatedTableItem,
} from '../../../../../../interfaces/paginated-table-data';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {AlertModal} from '../../../../../../components/alert-modal/alert-modal';
import {IndexViewScreen} from '../../../../../../components/index-view-screen/index-view-screen';
import {SystemEntitiesIndexListCard} from '../../../../../../components/system-entities-index-list-card/system-entities-index-list-card';
import AddSeniorFlow from '../modals/add-senior-flow/renderer';
import {useInfinityScrollItems, useObserveViewport} from '../../../../../../hooks';
import {ScrollFetchParams} from '../../../../../../hooks/infinite-scroll-items';
import {SystemEntitiesCardSkeleton} from '../../../../../../components/system-entities-index-list-card/skeleton';
import {SystemEntitiesIndexListItem} from '../../../../../../components/system-entities-index-list-item/system-entities-index-list-item';
import {SystemEntitiesRowSkeleton} from '../../../../../../components/system-entities-index-list-item/skeleton';
import {useAuth} from '../../../../../../contexts/auth/context';
import {useExportDataWithErrorHandler} from '../../../../../../hooks/use-export-data-with-error-handler';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {
  prepareSeniorsListPDF,
  prepareSeniorsListXLSX,
} from '../../../../../../utils/data/export-data/seniors/export-data-seniors';
import {exportSeniorsGetReportData} from '../../../../../../utils/data/export-data/seniors/base/export-seniors-get-report-data';
import {exportSeniorsProcessReportData} from '../../../../../../utils/data/export-data/seniors/base/export-seniors-process-report-data';
import {useSeniorProfilePdfExport} from '../../../../../../hooks/use-senior-profile-pdf-export';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Gender = 'gender',
  State = 'state',
}

export default function SeniorsProfessionalCaregiverOfficeScreen() {
  // Get params
  let {entityId}: {entityId?: string} = useParams();

  // Translations
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url, path} = useRouteMatch();

  // Get navigation info
  let history = useHistory();
  const {search: locationSearch} = useLocation();

  const {hasWritePermission, state: authState} = useAuth();
  const canWriteSeniorsList = hasWritePermission(RolePermissionType.Seniors_PersonalData_Profile);
  const canDeleteSeniorsList = hasWritePermission(RolePermissionType.Seniors_List);

  // Hold state
  const [deletedSeniorName, setDeletedSeniorName] = useState<string | null>(
    new URLSearchParams(locationSearch).get(ScreenPaths.SeniorsDeletedQuery)
  );

  const [onAddSeniorFlow, setOnAddSeniorFlow] = useState(false);

  // Fetch seniors
  const handleGetSeniors = useCallback(
    async ({limit, skip, search, filters, sort}: ScrollFetchParams) => {
      // Check for entity id
      if (!entityId) throw new Error('Empty entity id.');

      // Load data
      const {totalCount, data}: {totalCount: number; data: Array<AffiliatedUser>} =
        await UsersRepository.instance.getInEntity(
          entityId,
          limit,
          skip,
          UserType.Senior,
          search,
          true,
          filters,
          sort
        );

      // Process data
      const processedData: PaginatedTableData<AffiliatedUser> = data.map((obj) => {
        // Return row values
        return {
          id: obj.user.id || '',
          item: obj,
          enabled: obj.user.isActive,
          avatarUrl: obj.user.picture,
          toLink: `${url}/${obj.user.id}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
          columns: [
            {
              columnId: ColumnIdentifier.Name,
              value: obj.user.name || '',
            },
            {
              columnId: ColumnIdentifier.Gender,
              value: obj.user.gender ? t(translations.genders[obj.user.gender]) : '',
            },
            {
              columnId: ColumnIdentifier.State,
              value: obj.user.isActive
                ? t(translations.seniors.root.state_value_true)
                : t(translations.seniors.root.state_value_false),
            },
          ],
        };
      });

      // Return result
      return {
        data: processedData,
        totalCount,
      };
    },
    [entityId, t, url]
  );

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletedSeniorName(null);

    // Reset history
    history.replace(path.replace(':entityId', entityId || ''));
  };

  const toggleAddSeniorFlow = () => setOnAddSeniorFlow((state) => !state);

  const loaderRef = useRef() as MutableRefObject<HTMLButtonElement>;

  const {isRunningMobile} = useObserveViewport();
  const {previewPdfWithErrorHandling, previewXlsxWithErrorHandling} =
    useExportDataWithErrorHandler();

  const dataScrollInstance = useInfinityScrollItems<PaginatedTableItem<AffiliatedUser>>({
    fetchData: handleGetSeniors,
    pageSize: isRunningMobile ? 20 : 50,
    loaderRef,
  });

  const {currentSort, handleApplySort, handleApplyFilters} = dataScrollInstance;

  const handleApplySeniorFilters = useCallback(
    (filtersString?: string) => {
      const filterParams = new URLSearchParams(filtersString);
      filterParams.delete('sort');

      handleApplyFilters(filterParams.toString());
    },
    [handleApplyFilters]
  );

  useEffect(() => {
    if (!currentSort) {
      const initialSort = new URLSearchParams(locationSearch).get('sort') ?? 'name ASC';
      handleApplySort(initialSort);
    }
  }, [currentSort, handleApplySort, locationSearch]);

  const {handleExportPdf: handleExportPdfSeniorProfile} = useSeniorProfilePdfExport();

  const handleExportSeniorProfilePdf = useCallback(
    async (senior: AffiliatedUser) => {
      await handleExportPdfSeniorProfile({
        seniorId: senior.user.id,
        entity: authState.session?.selectedAffiliation?.entity,
      });
    },
    [authState.session?.selectedAffiliation?.entity, handleExportPdfSeniorProfile]
  );

  return (
    <>
      <IndexViewScreen
        pageHeader={{
          screenTitle: t(translations.seniors.root.title),
        }}
        searchOptions={{
          searchString: true,
          toggleDisplay: true,
          filters: true,
        }}
        dataScrollInstance={{
          ...dataScrollInstance,
          handleApplyFilters: handleApplySeniorFilters,
        }}
        emptyStateMessage={{
          title: t(translations.seniors.root.no_seniors),
          subtitle: t(translations.index_grid_screen.empty_message_add_encouraging, {
            type: t(translations.seniors.root.senior),
          }),
        }}
        rightColumn={({resultsLength}) => (
          <Stack gap={2} alignSelf="flex-end" alignItems="flex-end">
            {resultsLength ? (
              <Typography
                fontSize="14px"
                fontWeight={600}
                variant="caption"
                color="white"
                alignSelf="flex-end"
                sx={{maxWidth: '100%', paddingRight: '34px'}}
              >
                {t(translations.index_grid_screen.total_counter)}: {resultsLength}{' '}
                {t(
                  resultsLength === 1
                    ? translations.seniors.root.senior
                    : translations.seniors.root.seniors
                )}
              </Typography>
            ) : null}
            {canWriteSeniorsList && (
              <Button
                size="large"
                variant="inverted"
                startIcon={<AddIcon />}
                onClick={toggleAddSeniorFlow}
                sx={{
                  minWidth: 'unset',
                  width: isRunningMobile ? '60px' : '146px',
                  height: '40px',
                  '.MuiButton-startIcon': {
                    margin: {xs: 'inherit', md: 0},
                  },
                }}
              >
                {!isRunningMobile && t(translations.index_grid_screen.add_button)}
              </Button>
            )}
          </Stack>
        )}
        renderItem={{
          grid: {
            element: (props: any) => (
              <SystemEntitiesIndexListCard {...props} onExportPdf={handleExportSeniorProfilePdf} />
            ),
            skeleton: SystemEntitiesCardSkeleton,
            gridItemWidth: 200,
          },
          list: {
            element: (props: any) => (
              <SystemEntitiesIndexListItem {...props} onExportPdf={handleExportSeniorProfilePdf} />
            ),
            skeleton: SystemEntitiesRowSkeleton,
          },
        }}
        actions={{
          actions: [
            {
              label: t(translations.office.generics.save_pdf),
              icon: {element: SaveIcon},
              callback: async () => {
                await previewPdfWithErrorHandling(async () => {
                  const report = await exportSeniorsGetReportData({
                    entity: authState.session?.selectedAffiliation?.entity,
                    rawFilters: dataScrollInstance.currentFilters,
                  });
                  if (!report) {
                    return null;
                  }

                  const reportData = exportSeniorsProcessReportData({
                    reportData: report.reportData,
                  });

                  const pdf = prepareSeniorsListPDF({
                    reportData,
                    entity: report.entity,
                    t,
                  });

                  return {
                    file: pdf.file,
                    fileName: pdf.fileName,
                  };
                });
              },
            },
            {
              label: t(translations.office.generics.save_xlsx),
              icon: {element: SaveIcon},
              callback: async () => {
                await previewXlsxWithErrorHandling(async () => {
                  const report = await exportSeniorsGetReportData({
                    entity: authState.session?.selectedAffiliation?.entity,
                    rawFilters: dataScrollInstance.currentFilters,
                  });
                  if (!report) {
                    return null;
                  }

                  const reportData = exportSeniorsProcessReportData({
                    reportData: report.reportData,
                  });

                  const xlsx = prepareSeniorsListXLSX({
                    reportData,
                    entity: report.entity,
                    t,
                  });

                  return {
                    file: xlsx.file,
                    fileName: xlsx.fileName,
                  };
                });
              },
            },
          ],
        }}
        canWrite={canWriteSeniorsList}
        canDelete={canDeleteSeniorsList}
        loaderRef={loaderRef}
        viewType={UserType.Senior}
      />

      <AddSeniorFlow
        inFlow={onAddSeniorFlow}
        onClose={toggleAddSeniorFlow}
        triggerRefetch={dataScrollInstance.triggerRefetch}
      />

      <AlertModal
        id="senior-deleted-warning"
        opened={deletedSeniorName ? true : false}
        title={t(translations.seniors.root.deleted_senior_dialog_title, {
          name: deletedSeniorName,
        })}
        content={t(translations.seniors.root.deleted_senior_dialog_message)}
        handleClose={handleCloseDialog}
        actions={[
          {
            label: t(translations.seniors.root.deleted_senior_dialog_ok_button),
            type: 'neutral',
            handleClick: handleCloseDialog,
          },
        ]}
      />
    </>
  );
}
